import { useEffect, useState } from 'react';
import { SingleDatePicker } from 'react-dates';
import { ReactComponent as Close } from '../../assets/icons/trvto_closeWhite.svg';
import moment from 'moment';
import UserDetails from './constants/UserDetails';
import Price from './constants/Price';
import fetchTripData, { fetchTripDate } from './handleSubmit/fetchData';
import handleSubmit from './handleSubmit/handleSubmit';
import NoPeopleSelect from '../../pages/activityDetails/activityBookingTab/activityBookingTabFun/bookingTab/NoPeopleSelect';

export default function AddBookingAct({ actId, packData, setAddBo }) {
    const [bookingData, setBookingData] = useState({
        actId: actId,
        timeSlots: '',
        people: 1,
        totalSeats: [],
        amountPaid: {
            actual: 0,
            tax: 0,
            paid: null,
            reserve: [],
            total: 0,
            isReserved: false,
            isInstallment: false
        },
        userDetails: []
    });
    const [showSelector, setShowSelector] = useState(false);
    const [loading, setLoading] = useState(false);
    const [packDates, setPackDates] = useState([]);
    const isMobile = window.innerWidth < 768;
    const [timeSlots, setTimeSlots] = useState([]);
    const [focusedInput, setFocusedInput] = useState(null);
    const [selectedItems, setSelectedItems] = useState({});
    const [meetUpPoints, setMeetUpPoints] = useState([]);

    const totalCount = Object.values(selectedItems).reduce((sum, item) => sum + item.qty * item.count, 0);

    // Hydrate selectedItems from bookingData.totalSeats (e.g. edit mode), keyed by _id to match NoPeopleSelect
    useEffect(() => {
        if (bookingData?.totalSeats?.length > 0) {
            const formattedData = bookingData.totalSeats.reduce((acc, item) => {
                acc[item._id] = item;
                return acc;
            }, {});

            setSelectedItems(formattedData);
        }
    }, [bookingData?.totalSeats?.length]);

    // Push selectedItems back into bookingData
    useEffect(() => {
        const totalSeats = Object.values(selectedItems).map((item) => ({
            ...item
        }));
        const items = Object.values(selectedItems);
        const reserve = items.map((item) => ({
            name: item.name,
            amount: item.reserve
        }));
        setBookingData((prev) => ({
            ...prev,
            amountPaid: { ...prev.amountPaid, reserve },
            totalSeats,
            people: totalCount
        }));
    }, [selectedItems]);

    useEffect(() => {
        if (!bookingData?.date) return;

        const getTripDate = async () => {
            const dateOnly = moment(bookingData.date).format('YYYY-MM-DD');
            const tripDate = await fetchTripDate({ date: dateOnly, contentId: bookingData.actId }, 'act');
            const availableSlots = tripDate?.slots.filter(
                (slot) => slot.availableSlots > 0 && slot.totalSlots - slot.bookedSlots > 0
            );
            setBookingData((prev) => ({
                ...prev,
                actDatesId: tripDate?._id || ''
            }));

            setTimeSlots(availableSlots || []);
        };
        setMeetUpPoints(packData?.meetupLocation);
        getTripDate();
    }, [bookingData.date]);

    // Price calculation — driven entirely by selected seats/tiers
    useEffect(() => {
        if (!packData?.price) return;

        const seats = bookingData?.totalSeats || [];

        const actual = seats.reduce((sum, item) => sum + Math.floor(item.actual * item.qty), 0);
        const reserveTotal = seats.reduce((sum, item) => sum + item.reserve * item.qty, 0);
        const tax = Math.round(actual * 0.05);
        const total = actual + tax;

        setBookingData((prev) => ({
            ...prev,
            amountPaid: {
                ...prev.amountPaid,
                actual,
                tax,
                total
            }
        }));
    }, [bookingData.totalSeats, packData, bookingData.amountPaid.isReserved]);

    function handleUser(e, index) {
        if (Array.isArray(e)) {
            setBookingData((prevState) => ({
                ...prevState,
                userDetails: e
            }));
            return;
        }
        setBookingData((prevState) => ({
            ...prevState,
            userDetails: prevState?.userDetails?.length
                ? prevState.userDetails.map((obj, objIndex) =>
                      objIndex === index ? { ...obj, [e.target.name]: e.target.value } : obj
                  )
                : [{ [e.target.name]: e.target.value }]
        }));
    }

    function handleAmount(e, type, number = true) {
        const name = e.target.name;
        const value = number ? Number(e.target.value) : e.target.value;
        setBookingData((prevState) => ({
            ...prevState,
            [type]: {
                ...prevState[type],
                [name]: value
            }
        }));
    }

    return (
        <div>
            <div className='orgAddBoSubDiv'>
                {bookingData.actId && bookingData.actId !== '-1' && (
                    <>
                        <div className='orgSide dropRegDiv'>
                            <label className='dropRegLabel'>No. of People</label>
                            <div onClick={() => setShowSelector(true)} className='dropdownSelect'>
                                {bookingData.people || 'Select People'}
                            </div>
                            {showSelector && (
                                <div className='peopleOverlay' onClick={() => setShowSelector(false)}>
                                    <Close width={30} height={30} style={{ cursor: 'pointer' }} />
                                    <div className='peopleSheet' onClick={(e) => e.stopPropagation()}>
                                        <NoPeopleSelect
                                            data={packData.price}
                                            selectedItems={selectedItems}
                                            setSelectedItems={setSelectedItems}
                                            setShowSelector={setShowSelector}
                                            booking={bookingData}
                                            setBooking={setBookingData}
                                            packData={packData}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className='dropRegDiv'>
                            <label className='dropRegLabel'>Select Date</label>
                            <div className='dropRegInputDiv'>
                                <SingleDatePicker
                                    withPortal={isMobile}
                                    date={bookingData.date ? moment(bookingData.date) : null}
                                    onDateChange={(date) => {
                                        if (!date) return;

                                        setBookingData((prev) => ({
                                            ...prev,
                                            date: date.toISOString(),
                                            timeIndex: '',
                                            timeSlots: '',
                                            slotId: ''
                                        }));

                                        setFocusedInput(false);
                                    }}
                                    focused={focusedInput}
                                    onFocusChange={({ focused }) => setFocusedInput(focused)}
                                    displayFormat='DD-MM-YYYY'
                                    numberOfMonths={1}
                                    isOutsideRange={(day) => day.isBefore(moment().startOf('day'))}
                                    showDefaultInputIcon
                                    readOnly
                                />
                            </div>
                        </div>
                        {bookingData.date && (
                            <>
                                <div className='dropRegDiv'>
                                    <label className='dropRegLabel'>Select Time Slot</label>
                                    <div className='dropRegInputDiv'>
                                        <select
                                            value={bookingData.timeIndex ?? ''}
                                            name='timeSlots'
                                            onChange={(e) => {
                                                const index = e.target.value;
                                                if (index === '') return;
                                                const selectedSlot = timeSlots[index];

                                                setBookingData((prev) => ({
                                                    ...prev,
                                                    timeIndex: index,
                                                    timeSlots: {
                                                        startTime: selectedSlot.startTime,
                                                        endTime: selectedSlot.endTime
                                                    },
                                                    slotId: selectedSlot._id
                                                }));
                                            }}
                                            required={true}
                                            className='dropRegSelect'
                                        >
                                            {timeSlots.length === 0 && bookingData.date ? (
                                                <option value=''>No time slots available</option>
                                            ) : (
                                                <>
                                                    <option value=''>Select Time</option>

                                                    {timeSlots.map((val, index) => (
                                                        <option key={index} value={index}>
                                                            {`${val.startTime} - ${val.endTime}`}
                                                        </option>
                                                    ))}
                                                </>
                                            )}
                                        </select>
                                    </div>
                                </div>
                                <div className='dropRegDiv'>
                                    <label className='dropRegLabel'>Select Meetup Location</label>

                                    <div className='dropRegInputDiv'>
                                        <select
                                            value={bookingData?.meetupLocation?.name || ''}
                                            onChange={(e) => {
                                                const selectedLocation = meetUpPoints.find(
                                                    (item) => item.name === e.target.value
                                                );

                                                setBookingData((prev) => ({
                                                    ...prev,
                                                    meetupLocation: {
                                                        name: selectedLocation.name,
                                                        location: selectedLocation.location
                                                    }
                                                }));
                                            }}
                                            required
                                            className='dropRegSelect'
                                        >
                                            <option value='' disabled>
                                                Select Meetup Point
                                            </option>

                                            {meetUpPoints?.map((val, index) => (
                                                <option key={index} value={val.name}>
                                                    {val.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <Price
                                    bookingData={bookingData}
                                    handleAmount={handleAmount}
                                    isReservePrice={packData.price?.[0]?.reserve > 0}
                                />
                                {bookingData.people > 0 && (
                                    <UserDetails bookingData={bookingData} handleUser={handleUser} />
                                )}
                                <div className='orgAddBoBtn'>
                                    <button
                                        type='button'
                                        name='ManualBookingSubmit'
                                        className='orgAddBoSubmit blueBtn'
                                        onClick={() => handleSubmit('act', bookingData, packData, setAddBo)}
                                    >
                                        Submit
                                    </button>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
